import { useCallback, useEffect, useState } from 'react';
import {
  Loader,
  AlertCircle,
  GitBranch,
  Plus,
  Unlink,
  X,
  Search,
  ArrowRight,
  ArrowLeft,
  PartyPopper,
  Briefcase,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { eventTeamApiService } from '../../services/eventTeamApi';
import { useDebounce } from '../../hooks/useDebounce';
import type {
  EventEntityLink,
  EventEntityLinkType,
  EventEntityLinksResponse,
  EventItem,
  EventProject,
} from '../../types/eventTeam';
import { EVENT_ENTITY_LINK_TYPES } from '../../types/eventTeam';
import type { EventTeamPerms } from '../../utils/eventTeamPermissions';
import { cn } from '../../utils/cn';
import { SectionHeader } from '../ui/SectionHeader';
import { EmptyState } from '../ui/EmptyState';
import { SkeletonLoader } from '../SkeletonLoader';

const LINK_TYPE_LABELS: Record<EventEntityLinkType, string> = {
  related: 'Related to',
  depends_on: 'Depends on',
  blocks: 'Blocks',
  follow_up_of: 'Follow-up of',
  part_of_series: 'Part of series',
};

export interface EventEntityLinksSectionProps {
  sourceType: 'event' | 'project';
  sourceId: string;
  perms: EventTeamPerms;
  refreshNonce?: number;
  onChanged?: () => void;
}

/**
 * Related events/projects (guide §1.4): outgoing + incoming entity links with
 * typed relations (related / depends_on / blocks / follow_up_of / part_of_series).
 */
export const EventEntityLinksSection = ({
  sourceType,
  sourceId,
  perms,
  refreshNonce = 0,
  onChanged,
}: EventEntityLinksSectionProps) => {
  const navigate = useNavigate();
  const [links, setLinks] = useState<EventEntityLinksResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);

  // Add-link form
  const [showAdd, setShowAdd] = useState(false);
  const [targetType, setTargetType] = useState<'event' | 'project'>('event');
  const [linkType, setLinkType] = useState<EventEntityLinkType>('related');
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebounce(searchQuery, 300);
  const [searchResults, setSearchResults] = useState<(EventItem | EventProject)[]>([]);
  const [searching, setSearching] = useState(false);
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const data = await eventTeamApiService.getEntityLinks(sourceType, sourceId);
      setLinks(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load related links');
    } finally {
      setLoading(false);
    }
  }, [sourceId, sourceType]);

  useEffect(() => {
    setLoading(true);
    void load();
  }, [load]);

  useEffect(() => {
    if (refreshNonce === 0) return;
    void load();
  }, [refreshNonce, load]);

  useEffect(() => {
    if (!showAdd || debouncedSearch.trim().length === 0) {
      setSearchResults([]);
      return;
    }
    let cancelled = false;
    setSearching(true);
    const promise =
      targetType === 'event'
        ? eventTeamApiService.searchEvents(debouncedSearch)
        : eventTeamApiService.searchProjects(debouncedSearch);
    promise
      .then((res) => {
        if (!cancelled) setSearchResults(res);
      })
      .catch(() => {
        if (!cancelled) setSearchResults([]);
      })
      .finally(() => {
        if (!cancelled) setSearching(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedSearch, showAdd, targetType]);

  const handleAdd = async (targetId: string) => {
    setAdding(true);
    try {
      await eventTeamApiService.createEntityLink(sourceType, sourceId, {
        target_id: targetId,
        target_type: targetType,
        link_type: linkType,
      });
      setShowAdd(false);
      setSearchQuery('');
      setSearchResults([]);
      await load();
      onChanged?.();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to create link');
    } finally {
      setAdding(false);
    }
  };

  const handleUnlink = async (link: EventEntityLink, direction: 'outgoing' | 'incoming') => {
    if (!confirm('Remove this relation?')) return;
    const key = `${direction}-${link.target_id}-${link.source_id}`;
    setBusyKey(key);
    try {
      if (direction === 'outgoing') {
        await eventTeamApiService.deleteEntityLink(sourceType, sourceId, link.target_type, link.target_id);
      } else {
        // Incoming links are owned by the source entity.
        await eventTeamApiService.deleteEntityLink(link.source_type, link.source_id, sourceType as 'event' | 'project', sourceId);
      }
      await load();
      onChanged?.();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to remove link');
    } finally {
      setBusyKey(null);
    }
  };

  const navigateTo = (type: string, id: string) => {
    navigate(type === 'event' ? `/event-team/events/${id}` : `/event-team/projects/${id}`);
  };

  if (loading) {
    return (
      <div className="space-y-2" aria-busy="true">
        <SkeletonLoader className="h-4 w-32" />
        {Array.from({ length: 3 }).map((_, i) => (
          <SkeletonLoader key={i} className="h-11 w-full" />
        ))}
      </div>
    );
  }

  const outgoing = links?.outgoing ?? [];
  const incoming = links?.incoming ?? [];

  const renderLink = (link: EventEntityLink, direction: 'outgoing' | 'incoming') => {
    const otherType = direction === 'outgoing' ? link.target_type : link.source_type;
    const otherId = direction === 'outgoing' ? link.target_id : link.source_id;
    const key = `${direction}-${link.target_id}-${link.source_id}`;
    const busy = busyKey === key;
    return (
      <div key={key + link.link_type} className="subcard flex items-center justify-between gap-2 px-3 py-2 flex-wrap">
        <div className="flex items-center gap-2 min-w-0 flex-wrap">
          {direction === 'outgoing' ? (
            <ArrowRight className="w-3.5 h-3.5 text-accent-400 flex-shrink-0" />
          ) : (
            <ArrowLeft className="w-3.5 h-3.5 text-purple-400 flex-shrink-0" />
          )}
          <span className="badge badge-neutral capitalize">{LINK_TYPE_LABELS[link.link_type] ?? link.link_type}</span>
          <button
            type="button"
            onClick={() => navigateTo(otherType, otherId)}
            className="text-sm text-accent-300 hover:text-accent-200 hover:underline underline-offset-2 transition-colors truncate inline-flex items-center gap-1.5 min-w-0"
          >
            {otherType === 'event' ? (
              <PartyPopper className="w-3.5 h-3.5 shrink-0" />
            ) : (
              <Briefcase className="w-3.5 h-3.5 shrink-0" />
            )}
            <span className="truncate">{link.title || otherId}</span>
          </button>
          {link.status && (
            <span className="badge badge-neutral capitalize">{String(link.status).replace(/_/g, ' ')}</span>
          )}
        </div>
        {perms.canCreate && (
          <button
            type="button"
            onClick={() => void handleUnlink(link, direction)}
            disabled={busy}
            className="btn-icon btn-icon-sm text-red-400 hover:text-red-300 flex-shrink-0"
            title="Remove relation"
            aria-label="Remove relation"
          >
            {busy ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <Unlink className="w-3.5 h-3.5" />}
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-3">
      <SectionHeader
        title="Related"
        icon={GitBranch}
        iconClassName="text-violet-400"
        count={outgoing.length + incoming.length}
        className="mb-0 flex-wrap"
        actions={
          perms.canCreate ? (
            <button type="button" onClick={() => setShowAdd(!showAdd)} className="glass-button-sm" aria-expanded={showAdd}>
              <Plus className="w-3.5 h-3.5" />
              Add relation
            </button>
          ) : undefined
        }
      />

      {error && (
        <div className="alert alert-danger" role="alert">
          <AlertCircle />
          <span className="flex-1">{error}</span>
        </div>
      )}

      {showAdd && (
        <div className="subcard p-3 space-y-2">
          <div className="flex items-center gap-2 flex-wrap">
            <select
              value={linkType}
              onChange={(e) => setLinkType(e.target.value as EventEntityLinkType)}
              className="glass-select text-sm"
              aria-label="Relation type"
            >
              {EVENT_ENTITY_LINK_TYPES.map((t) => (
                <option key={t} value={t}>
                  {LINK_TYPE_LABELS[t]}
                </option>
              ))}
            </select>
            <select
              value={targetType}
              onChange={(e) => {
                setTargetType(e.target.value as 'event' | 'project');
                setSearchResults([]);
              }}
              className="glass-select text-sm"
              aria-label="Target type"
            >
              <option value="event">Event</option>
              <option value="project">Project</option>
            </select>
            <div className="relative flex-1 min-w-[180px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-glass-400 pointer-events-none" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={`Search ${targetType}s…`}
                className="glass-input w-full pl-9"
                autoFocus
              />
              {searching && (
                <Loader className="absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 animate-spin text-glass-400" />
              )}
            </div>
            <button
              type="button"
              onClick={() => setShowAdd(false)}
              className="btn-icon btn-icon-sm"
              title="Close"
              aria-label="Close relation picker"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
          {searchResults.length > 0 && (
            <div className="space-y-1 max-h-[200px] overflow-y-auto">
              {searchResults
                .filter((r) => !(r.id === sourceId && targetType === sourceType))
                .map((result) => (
                  <button
                    key={result.id}
                    onClick={() => void handleAdd(result.id)}
                    disabled={adding}
                    className={cn(
                      'w-full flex items-center justify-between gap-2 px-2 py-1.5 rounded-md text-left',
                      'hover:bg-glass-200/10 transition-colors',
                    )}
                  >
                    <span className="text-sm text-white truncate">{result.title}</span>
                    <span className="text-[10px] text-glass-500 capitalize flex-shrink-0">
                      {String(result.status).replace(/_/g, ' ')}
                    </span>
                  </button>
                ))}
            </div>
          )}
        </div>
      )}

      {outgoing.length === 0 && incoming.length === 0 && !error ? (
        <EmptyState bare compact icon={GitBranch} title="No related events or projects" />
      ) : (
        <div className="space-y-3">
          {outgoing.length > 0 && (
            <div className="space-y-2">
              <p className="eyebrow">Outgoing</p>
              {outgoing.map((link) => renderLink(link, 'outgoing'))}
            </div>
          )}
          {incoming.length > 0 && (
            <div className="space-y-2">
              <p className="eyebrow">Incoming</p>
              {incoming.map((link) => renderLink(link, 'incoming'))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
